import React, { useState } from 'react';
import { auth, provider, db } from '../firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import generateUserAgreement from '../userAgreement';
import AgreementModal from './AgreementModal';
import generateKvkkText from '../KVKK';
import KVKKModal from './KVKKModal';
import './LoginPage.css';

// Hata kodlarını Türkçe ve kullanıcı dostu metinlere çeviren yardımcı fonksiyon
const getFirebaseErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-credential':
      return 'Girdiğiniz e-posta adresi veya şifre hatalı. Lütfen bilgilerinizi kontrol edip tekrar deneyin.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'E-posta adresi veya şifre hatalı.';
    case 'auth/email-already-in-use':
      return 'Bu e-posta adresi sistemimizde zaten kayıtlı. Lütfen giriş yapmayı deneyin veya şifrenizi sıfırlayın.';
    case 'auth/weak-password':
      return 'Şifreniz çok zayıf. Lütfen en az 6 karakterden oluşan daha güvenli bir şifre belirleyin.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız deneme yaptınız. Güvenliğiniz için hesabınız geçici olarak kilitlendi, lütfen daha sonra tekrar deneyin.';
    case 'auth/invalid-email':
      return 'Lütfen geçerli bir e-posta adresi formatı girdiğinizden emin olun.';
    default:
      return `Beklenmedik bir hata oluştu. Lütfen daha sonra tekrar deneyin. (Hata Kodu: ${errorCode})`;
  }
};

function LoginPage() {
  const [isRegistering, setIsRegistering] = useState(true);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showKVKK, setShowKVKK] = useState(false);

  const emailDomain = email.split('@')[1] || '';
  const isValidEmail = email.includes('@') && emailDomain.includes('.');

  const handleEmailSubmit = async () => {
    const isValidEmail = email.includes('@') && email.split('@')[1]?.includes('.');

    if (!email || !password || (isRegistering && (!name || !company))) {
      alert('Lütfen tüm gerekli alanları doldurun.');
      return;
    }

    if (!isValidEmail) {
      alert('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    if (isRegistering && !agreementAccepted) {
      alert('Devam etmek için kullanıcı sözleşmesini kabul etmelisiniz.');
      return;
    }

    try {
      if (isRegistering) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await updateProfile(user, { displayName: name });
        await sendEmailVerification(user);

        await setDoc(doc(db, 'users', user.uid), {
          name,
          company,
          email: user.email,
          idea_credit: 1,
          project_credit: 0,
          createdAt: new Date()
        });

        const agreementText = generateUserAgreement({
          name,
          company,
          email,
          date: new Date().toLocaleDateString('tr-TR')
        });

        const kvkkText = generateKvkkText({
          name,
          company,
          email,
          date: new Date().toLocaleDateString('tr-TR')
        });

        await setDoc(doc(db, 'agreements', user.uid), {
          userId: user.uid,
          name,
          company,
          email: user.email,
          agreementText,
          agreedAt: new Date(),
        });

        await setDoc(doc(db, 'KVKK', user.uid), {
          userId: user.uid,
          name,
          company,
          email: user.email,
          kvkkText,
          agreedAt: new Date(),
        });

        alert('Kayıt başarılı! Lütfen e-posta adresinizi doğrulamak için e-posta kutunuzu kontrol edin.');
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        if (!user.emailVerified) {
          alert('Lütfen e-posta adresinizi doğrulayın.');
          return;
        }

        // Giriş başarılı (Yönlendirme vb. işlemleri buraya ekleyebilirsiniz)
      }
    } catch (err) {
      // Hata mesajını Türkçe metne çeviriyoruz
      alert(getFirebaseErrorMessage(err.code));
    }
  };

  const handlePasswordReset = async () => {
    if (!isValidEmail) {
      alert('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      alert('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.');
      setIsResettingPassword(false);
    } catch (err) {
      // Şifre sıfırlama ekranındaki hataları da çeviriyoruz
      alert(getFirebaseErrorMessage(err.code));
    }
  };

  return (
    <div className="center-screen">
      {/*<LogoBanner />*/}
      <Banner />

      {isRegistering && (
        <>
          <input
            type="name"
            className="form-input"
            placeholder="Ad Soyad"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="name"
            className="form-input"
            placeholder="Şirket Adı"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </>
      )}

      <input
        type="email"
        className="form-input"
        placeholder="E-posta"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      {!isResettingPassword && (
        <input
          type="password"
          className="form-input"
          placeholder="Şifre"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      )}

      {isRegistering && name && company && isValidEmail && (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
          <input
            type="checkbox"
            id="agreement"
            checked={agreementAccepted}
            onChange={(e) => setAgreementAccepted(e.target.checked)}
            style={{ marginTop: '5px', marginRight: '8px' }}
          />
          <label htmlFor="agreement" style={{ fontSize: '14px' }}>
            <button
              type="button"
              onClick={() => setShowAgreement(true)}
              style={{
                color: '#003c96',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }}
            >
              Kullanıcı sözleşmesini 
            </button>{' '}
            ve{' '} 
            <button
              type="button"
              onClick={() => setShowKVKK(true)}
              style={{
                color: '#003c96',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }}
            >
                KVKK bilgilendirme metnini
            </button>{' '}
            okudum ve kabul ediyorum.
          </label>
        </div>
      )}

      {!isResettingPassword ? (
        <button className="register-btn" onClick={handleEmailSubmit}>
          {isRegistering ? 'Kayıt Ol' : 'Email ile Giriş Yap'}
        </button>
      ) : (
        <>
          <p style={{ fontSize: '14px', marginTop: '10px' }}>
            Şifre sıfırlama bağlantısını almak için e-posta adresinizi girin.
          </p>
          <button className="register-btn" onClick={handlePasswordReset}>
            Şifreyi Sıfırla
          </button>
        </>
      )}

      {!isRegistering && !isResettingPassword && (
        <button
          onClick={() => setIsResettingPassword(true)}
          style={{
            marginTop: '10px',
            fontSize: '14px',
            background: 'none',
            border: 'none',
            color: '#003c96',
            cursor: 'pointer'
          }}
        >
          Şifrenizi mi unuttunuz?
        </button>
      )}

      {!isRegistering && isResettingPassword && (
        <button
          onClick={() => setIsResettingPassword(false)}
          style={{
            marginTop: '10px',
            fontSize: '14px',
            background: 'none',
            border: 'none',
            color: '#003c96',
            cursor: 'pointer'
          }}
        >
          Geri dön
        </button>
      )}

      <button
        onClick={() => {
          setIsRegistering(!isRegistering);
          setIsResettingPassword(false);
        }}
        style={{
          marginTop: '1%',
          fontSize: '16px',
          background: 'none',
          border: 'none',
          color: '#003c96',
          cursor: 'pointer',
          bottom: '1%'
        }}
      >
        {isRegistering ? 'Zaten hesabınız var mı? Giriş yap' : 'Hesabınız yok mu? Kayıt ol'}
      </button>

      {showAgreement && (
        <AgreementModal
          onClose={() => setShowAgreement(false)}
          name={name}
          company={company}
          email={email}
        />
      )}{showKVKK && (
        <KVKKModal
          onClose={() => setShowKVKK(false)}
          name={name}
          company={company}
          email={email}
        />
      )}
    </div>
  );
}

function Banner() {
  return (
    <div className="banner">
      <h1>ProWA</h1>
      <h2>Proje Yazım Asistanı</h2>
      <p>ProWA bir Ideatrum uygulamasıdır.</p>
    </div>
  );
}

export default LoginPage;